import { useEffect, useRef } from "react";

import * as Sentry from "@sentry/react";

import type { MapMode } from "@/features/map/contexts/MapControlContext";
import {
  isAddressParamTooLong,
  parseMapUrlParams,
} from "@/features/map/utils/urlParams/parseMapUrlParams";
import { resolveAutoSearchTarget } from "@/features/map/utils/urlParams/resolveAutoSearchTarget";

/**
 * `useMapUrlParams` の引数インターフェース。
 */
export interface UseMapUrlParamsArgs {
  /** 現在の地図モード。null は未確定（契約取得処理が完了していない）。 */
  mapMode: MapMode;
  /**
   * ゼンリン契約有無 / GEOSPACE 契約有無。
   *
   * 親 `ChibanViewerAccessControl`（features/map/ChibanViewerAccessControl.tsx）が
   * `isContractLoading || !contractInfo` の間 CircularProgress を表示し、契約確定後にのみ
   * ChibanViewer をマウントするため、これらの props は常に確定値でマウントされる前提。
   * したがって本 hook は契約ロード中状態（取得前 false）を考慮しない。
   *
   * 注意: 将来 親の構造が変わり契約確定前にマウントされるようになると、`hasZenrinContract`/
   * `hasGeospaceContract` が一時的に false となり、address ディープリンクが「契約なし=無料検索」
   * に降格しうる。その場合は契約ロード中フラグの受け渡しを検討すること。
   */
  hasZenrinContract: boolean;
  hasGeospaceContract: boolean;
  /** 住所テキストボックスの setter（React state setter）。 */
  setNormalAddressTextBox: React.Dispatch<React.SetStateAction<string>>;
  /**
   * 住所自動検索のトリガ。
   * `predictedMapMode` は `predictMapModeAfterChibanchizu` が返す「次レンダリング後の予想 mapMode」。
   */
  triggerAddressSearch: (
    address: string,
    predictedMapMode: MapMode
  ) => Promise<void>;
  /** 登記地番テキストボックスの setter（React state setter）。 */
  setChibanAddressTextBox: React.Dispatch<React.SetStateAction<string>>;
  /**
   * 登記地番自動検索のトリガ（Salesforce SSO 引き継ぎ用途）。
   * 検索語は `chiban` 優先・無ければ `kaokuNumber`（`resolveAutoSearchTarget` が解決済み）。
   */
  triggerChibanSearch: (searchTerm: string) => Promise<void>;
  /**
   * `address` パラメータが上限超過で棄却されたときに呼ばれるコールバック（任意）。
   * 「空・未指定」とは区別し、ユーザーへの警告通知（`toast.warning` 等）に用いる。
   * `processedRef` の二重実行ガードにより初回マウント時に最大 1 回のみ呼ばれる。
   */
  onAddressTooLong?: () => void;
}

/**
 * `chibanchizu=true` 指定時に、レイヤー反映後に予想される mapMode を算出する純粋関数。
 * `triggerAddressSearch` の API 種別判定（geospace / zenrin）に使用する。
 *
 * レイヤー反映自体は同期経路 `computeUrlBasedCheckedLayers`（ChibanViewer）が担うため、
 * 本関数は副作用を持たず予想 mapMode を返すだけに留める。
 *
 * - GEOSPACE 契約あり → "geospace" / ゼンリンのみ → "zenrin" / それ以外 → 現在値を維持。
 */
const predictMapModeAfterChibanchizu = (
  chibanchizu: boolean,
  hasGeospaceContract: boolean,
  hasZenrinContract: boolean,
  currentMapMode: MapMode
): MapMode => {
  if (!chibanchizu || (!hasGeospaceContract && !hasZenrinContract)) {
    return currentMapMode;
  }
  return hasGeospaceContract ? "geospace" : "zenrin";
};

/**
 * 地図画面の URL クエリパラメータ（address / chiban / kaokuNumber / chibanchizu /
 * requestedPicturesList ほか）を解析し、address または chiban の自動検索を実行する hook。
 *
 * 処理内容:
 *   1. `resolveAutoSearchTarget` で自動検索の対象を 1 つに絞り込む
 *      （優先順位: `address` > 地番検索（`chiban` 優先・無ければ `kaokuNumber`） > なし。実装計画書 4.3 節）
 *   2a. 対象が `address`: chibanchizu から予想 mapMode を算出し、address 自動検索（await）
 *   2b. 対象が `chiban`: 解決済みの検索語で地番自動検索（await）
 *
 * レイヤー反映（chibanchizu / requestedPicturesList）は LeafletMap の `overlayVisibility`
 * 遅延初期化により非同期 `setCheckedLayers` が描画へ伝播しないため、本 hook では行わず、
 * 同期経路 `computeUrlBasedCheckedLayers`（ChibanViewer）が初期 `checkedLayers` に組み込む。
 *
 * - `mapMode === null` かつ契約ありの場合は処理を保留（契約情報の確定を待つ）。
 * - 内部の `processedRef` により、初回処理完了後は再レンダリングしても再実行しない。
 *   処理対象は初回マウント時点の `window.location.search` のみで、マウント中に SPA 内で
 *   URL（クエリ）が変化しても再処理しない（「初回マウント URL のみ処理」の契約）。
 * - `triggerAddressSearch` / `triggerChibanSearch` が想定外の例外を throw した場合は
 *   Sentry に通知する。例外発生時も `processedRef` は立ったままのため、同一マウント内での
 *   再試行は行わない。
 * - `chiban` が無く `kaokuNumber` だけの場合は `kaokuNumber` を検索語に用いる。家屋番号からの
 *   地番変換は将来対応のため座標解決までは行わず、検索欄に値が入った状態で利用者の操作を待つ
 *   （実装計画書 4.3 節）。
 *
 * 新しいパラメータを追加する場合は:
 *   1. `utils/urlParams/schema.ts` の `MAP_URL_PARAM_SCHEMA` にキー定義を追加
 *   2. 必要なら `UseMapUrlParamsArgs` にハンドラを追加
 *   3. 本 hook の useEffect 内に処理分岐を追加
 */
export const useMapUrlParams = (args: UseMapUrlParamsArgs): void => {
  const {
    mapMode,
    hasZenrinContract,
    hasGeospaceContract,
    setNormalAddressTextBox,
    triggerAddressSearch,
    setChibanAddressTextBox,
    triggerChibanSearch,
    onAddressTooLong,
  } = args;

  const processedRef = useRef<boolean>(false);

  useEffect(() => {
    // 二重実行ガード
    if (processedRef.current) return;

    // mapMode 確定待ち（契約があるのに mapMode が null = 契約取得後の確定待ち）
    if (mapMode === null && (hasZenrinContract || hasGeospaceContract)) {
      return;
    }

    const params = parseMapUrlParams(window.location.search);

    // address が上限超過で棄却された場合は、サイレントに無視せずユーザーへ警告する。
    // address のみ無視し、chibanchizu / requestedPicturesList の適用（同期経路）は妨げない。
    if (isAddressParamTooLong(window.location.search)) {
      onAddressTooLong?.();
    }

    // 全パラメータが効果なしの場合は早期 return
    if (
      !params.address &&
      !params.chibanchizu &&
      !params.requestedPicturesList &&
      !params.chiban &&
      !params.kaokuNumber
    ) {
      processedRef.current = true;
      return;
    }

    // 非同期処理の完了を待たずに、ここで同期的にガードを立てる。
    // address 自動検索（triggerAddressSearch）は地図移動などで mapMode（この effect の
    // 依存）を変化させ得るため、await 中に effect が再実行されうる。ガードを async 内で
    // 立てると再実行時にまだ false で二重処理されるため、処理開始を確定した時点で立てる。
    processedRef.current = true;

    void (async () => {
      // 自動検索の対象を 1 つに絞り込む（優先順位: address > 地番検索（chiban 優先・無ければ kaokuNumber） > なし）。
      const target = resolveAutoSearchTarget({
        address: params.address,
        chiban: params.chiban,
        kaokuNumber: params.kaokuNumber,
      });

      if (target.kind === "address") {
        // chibanchizu レイヤー反映後に予想される mapMode を算出する。
        // レイヤー反映自体は同期経路 computeUrlBasedCheckedLayers（ChibanViewer）が担うため、
        // ここでは address 検索の API 種別判定に使う予想 mapMode の算出のみを行う。
        const predictedMapMode = predictMapModeAfterChibanchizu(
          params.chibanchizu,
          hasGeospaceContract,
          hasZenrinContract,
          mapMode
        );

        // テキストボックスへの反映 + 検索トリガを呼び出す。
        // 契約有無による分岐（契約なしは近辺移動のみ等）は呼び出し側（ChibanViewer の
        // `triggerAddressSearch` 内）の責務とし、本 hook は呼び出すだけに留める。
        setNormalAddressTextBox(target.address);
        try {
          await triggerAddressSearch(target.address, predictedMapMode);
        } catch (error) {
          Sentry.captureException(error);
        }
        return;
      }

      if (target.kind === "chiban") {
        // テキストボックスへの反映 + 検索トリガを呼び出す。
        // 検索語は chiban 優先・無ければ kaokuNumber（resolveAutoSearchTarget が解決済み）。
        setChibanAddressTextBox(target.searchTerm);
        try {
          await triggerChibanSearch(target.searchTerm);
        } catch (error) {
          Sentry.captureException(error);
        }
      }
    })();
    // 依存配列は既存 stateKey useEffect と同じ。setter 群は安定参照前提（React useState の標準挙動）。
  }, [mapMode, hasZenrinContract, hasGeospaceContract]);
};
